import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cx } from './cx';

/**
 * Typography primitives. You pick a ROLE, never a raw size - size, leading,
 * weight, and tracking are bound to the role in ds.css (@theme text-*).
 *
 * Heading: display | h1 | h2 | h3 | h4 - all Gotham, all rendered CAPS.
 *          h4 is h3's twin (same size) but Book weight, not bold - the
 *          "subheading" cut.
 * Text:    lede · body · small · caption · eyebrow · label (Gotham Narrow).
 */

type HeadingLevel = 'display' | 'h1' | 'h2' | 'h3' | 'h4';

// Every heading is uppercase Gotham (the brand title voice). Case is applied
// here so it holds on pages that are not under [data-ds] too.
const HEADING_ROLE: Record<HeadingLevel, string> = {
  display: 'text-display font-display text-ink uppercase',
  h1: 'text-h1 font-display text-ink uppercase',
  h2: 'text-h2 font-block text-text uppercase',
  h3: 'text-h3 font-block text-text uppercase',
  h4: 'text-h4 font-block text-text uppercase',
};
const HEADING_TAG: Record<HeadingLevel, ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
};

export function Heading({
  level = 'h2',
  as,
  className,
  children,
  ...rest
}: {
  level?: HeadingLevel;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<'h2'>, 'className' | 'children'>) {
  const Tag = (as ?? HEADING_TAG[level]) as ElementType;
  return (
    <Tag className={cx(HEADING_ROLE[level], className)} {...rest}>
      {children}
    </Tag>
  );
}

type TextRole = 'lede' | 'body' | 'small' | 'caption' | 'eyebrow' | 'label';

const TEXT_ROLE: Record<TextRole, string> = {
  lede: 'text-lede font-body',
  body: 'text-body font-body',
  small: 'text-small font-body',
  caption: 'text-caption font-body',
  eyebrow: 'text-eyebrow font-body uppercase',
  label: 'text-label font-body uppercase',
};

export function Text({
  role = 'body',
  muted = false,
  faint = false,
  prose = false,
  as,
  className,
  children,
  ...rest
}: {
  role?: TextRole;
  muted?: boolean;
  faint?: boolean;
  /** Cap the reading measure (~68ch) so body copy never runs full-width. */
  prose?: boolean;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<'p'>, 'className' | 'children' | 'role'>) {
  const Tag = (as ?? (role === 'eyebrow' || role === 'label' ? 'span' : 'p')) as ElementType;
  return (
    <Tag
      className={cx(
        TEXT_ROLE[role],
        faint ? 'text-text-faint' : muted ? 'text-text-muted' : 'text-text',
        prose && 'max-w-[68ch]',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
